import React, { useState, useEffect } from 'react';
import Button from '@/components/common/Button';
import AddressAutocomplete from '@/components/common/AddressAutocomplete';
import { HorarioSelectorCompact } from '@/components/common/HorarioSelectorCompact';
import { FaBuilding, FaMapMarkerAlt, FaPhone, FaUsers, FaClock, FaSave } from 'react-icons/fa';
import { normalizeDataObject } from '../../utils/normalizationUtils';
import ValidatedInput from '@/components/common/ValidatedInput';
import { usePlanValidation } from '@/hooks/usePlanValidation';
import QuotaWarning from '@/components/common/QuotaWarning';
import PaymentModal from '@/components/payments/PaymentModal';

/**
 * Formulario para crear o editar sucursales
 */
const SucursalForm = ({ 
  initialData = {}, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  // Hook para validación de cuotas
  const {
    validateQuota,
    getResourceUsage,
    showPaymentModal,
    targetPlanId,
    closePaymentModal,
    openPaymentModal
  } = usePlanValidation();
  const [formData, setFormData] = useState({
    Nombre: '',
    Direccion: '',
    Telefono: '',
    HorarioAtencion: '',
    Ciudad: '',
    horarios: null,
    CapacidadClientes: 1
  });

  // Si hay datos iniciales, actualizar el estado del formulario
  useEffect(() => {
    if (initialData && Object.keys(initialData).length) {
      // Normalizar datos iniciales para manejar cualquier formato
      const normalizedData = normalizeDataObject(initialData, 'sucursal');
      
      let horariosData = null;
      
      // Intentar parsear el campo horarios si existe con mejor manejo de errores
      const horariosField = normalizedData.horarios || normalizedData.Horarios || normalizedData.HorarioAtencion;
      console.log('🕐 [SucursalForm] Horarios field received:', horariosField);
      
      if (horariosField) {
        try {
          if (typeof horariosField === 'string') {
            // Verificar si es un JSON válido antes de parsear
            if (horariosField.trim().startsWith('{') || horariosField.trim().startsWith('[')) {
              horariosData = JSON.parse(horariosField);
              console.log('✅ [SucursalForm] Horarios parsed successfully:', horariosData);
            } else {
              console.warn('⚠️ [SucursalForm] Horarios field is not valid JSON, using as text:', horariosField);
              // Si no es JSON, dejar que el componente maneje el texto
              horariosData = null;
            }
          } else if (typeof horariosField === 'object' && horariosField !== null) {
            horariosData = horariosField;
            console.log('✅ [SucursalForm] Horarios is already an object:', horariosData);
          } else {
            console.warn('⚠️ [SucursalForm] Horarios field has unexpected type:', typeof horariosField);
            horariosData = null;
          }
        } catch (e) {
          console.error('❌ [SucursalForm] Error parsing horarios:', e);
          console.error('❌ [SucursalForm] Original field value:', horariosField);
          horariosData = null;
        }
      }
      
      // Generar texto de horario seguro
      let horarioTexto = '';
      if (horariosData) {
        horarioTexto = generarTextoHorario(horariosData);
      } else {
        // Si no se pudo parsear horariosData, intentar usar el texto existente
        const existingHorario = normalizedData.horarioAtencion || normalizedData.HorarioAtencion || '';
        // Solo usar si no es el mensaje de error
        horarioTexto = existingHorario.includes('Error en horario') ? '' : existingHorario;
      }
      
      setFormData({
        Nombre: normalizedData.nombre || normalizedData.Nombre || '',
        Direccion: normalizedData.direccion || normalizedData.Direccion || '',
        Telefono: normalizedData.telefono || normalizedData.Telefono || '',
        HorarioAtencion: horarioTexto,
        Ciudad: normalizedData.ciudad || normalizedData.Ciudad || '',
        horarios: horariosData,
        CapacidadClientes: normalizedData.capacidadClientes || normalizedData.CapacidadClientes || 1
      });
      
      console.log('📝 [SucursalForm] Form data set:', {
        horarios: horariosData,
        HorarioAtencion: horarioTexto
      });
    }
  }, [initialData]);

  // Manejar cambios en el formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Manejar cambios en el horario
  const handleHorarioChange = (horarios) => {
    setFormData(prev => ({
      ...prev,
      horarios: horarios,
      HorarioAtencion: generarTextoHorario(horarios)
    }));
  };

  // Función para generar texto legible del horario con mejor manejo de errores
  const generarTextoHorario = (horarios) => {
    if (!horarios) return '';
    
    try {
      // Verificar que horarios sea un objeto válido
      if (typeof horarios !== 'object' || Array.isArray(horarios)) {
        console.warn('⚠️ [SucursalForm] Horarios is not a valid object:', horarios);
        return '';
      }
      
      const diasSemana = [
        { key: 'lunes', label: 'Lun' },
        { key: 'martes', label: 'Mar' },
        { key: 'miercoles', label: 'Mié' },
        { key: 'jueves', label: 'Jue' },
        { key: 'viernes', label: 'Vie' },
        { key: 'sabado', label: 'Sáb' },
        { key: 'domingo', label: 'Dom' }
      ];
      
      const diasTexto = [];
      
      diasSemana.forEach(({ key, label }) => {
        try {
          const dia = horarios[key];
          if (!dia || !dia.activo) return;
          
          const turnos = [];
          
          // Verificar turno mañana con validaciones adicionales
          if (dia.mañana && 
              typeof dia.mañana === 'object' && 
              dia.mañana.activo && 
              dia.mañana.inicio && 
              dia.mañana.fin) {
            turnos.push(`${dia.mañana.inicio}-${dia.mañana.fin}`);
          }
          
          // Verificar turno tarde con validaciones adicionales
          if (dia.tarde && 
              typeof dia.tarde === 'object' && 
              dia.tarde.activo && 
              dia.tarde.inicio && 
              dia.tarde.fin) {
            turnos.push(`${dia.tarde.inicio}-${dia.tarde.fin}`);
          }
          
          if (turnos.length > 0) {
            diasTexto.push(`${label}: ${turnos.join(', ')}`);
          }
        } catch (diaError) {
          console.warn(`⚠️ [SucursalForm] Error processing day ${key}:`, diaError);
          // Continuar con el siguiente día
        }
      });
      
      return diasTexto.join(' | ');
    } catch (error) {
      console.error('❌ [SucursalForm] Error generating horario text:', error);
      return 'Error en horario (json)';
    }
  };

  // Manejar envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Solo validar cuota para nuevas sucursales (no para edición)
    const isEditing = initialData && initialData.id;
    
    if (!isEditing) {
      // Validar cuota de sucursales antes de crear
      const canCreateSucursal = validateQuota('sucursales', {
        showToast: false,
        showModal: true
      });
      
      if (!canCreateSucursal) {
        //console.log('🚫 No se puede crear sucursal - límite alcanzado');
        return; // Bloquear el envío del formulario
      }
    }
    
    // Normalizar datos antes de enviar
    const normalizedFormData = normalizeDataObject(formData, 'sucursal');
    
    // Asegurar que se incluya el campo horarios como JSON
    if (formData.horarios) {
      normalizedFormData.horarios = JSON.stringify(formData.horarios);
      console.log('📤 [SucursalForm] Sending horarios JSON:', normalizedFormData.horarios);
    }
    
    console.log('📤 [SucursalForm] Final form data to submit:', normalizedFormData);
    
    onSubmit(normalizedFormData);
  };

  // Obtener métricas de uso para sucursales
  const sucursalesUsage = getResourceUsage('sucursales');
  const isEditing = initialData && initialData.id;

  return (
    <form onSubmit={handleSubmit} className="text-white">
      {/* Mostrar advertencia de cuota solo para nuevas sucursales */}
      {!isEditing && sucursalesUsage && (
        <QuotaWarning
          resourceType="sucursales"
          usageMetric={sucursalesUsage}
          onUpgrade={() => openPaymentModal()}
          className="mb-6"
        />
      )}
      
      <div className="md:grid md:grid-cols-2 md:gap-6">
        {/* Columna izquierda: Información Básica */}
        <div className="bg-gray-800 p-4 rounded-lg mb-6 md:mb-0 border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaBuilding className="text-blue-400 mr-2" />
            Información Básica
          </h3>
          
          {/* Nombre de la Sucursal */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaBuilding className="text-gray-400 mr-2 text-xs" />
              Nombre de la Sucursal<span className="text-red-500 ml-1">*</span>
            </label>
            <ValidatedInput
              type="text"
              name="Nombre"
              value={formData.Nombre}
              onChange={handleChange}
              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Nombre de la Sucursal"
              required
              requiredMessage='Nombre de la sucursal es requerido'
            />
          </div>

          {/* Teléfono y Capacidad en la misma fila */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            {/* Teléfono */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaPhone className="text-gray-400 mr-2 text-xs" />
                Teléfono
              </label>
              <input
                type="text"
                name="Telefono"
                value={formData.Telefono}
                onChange={handleChange}
                className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                placeholder="Teléfono"
              />
            </div>

            {/* Capacidad de Clientes */}
            <div>
              <label className="flex items-center text-sm font-medium mb-2">
                <FaUsers className="text-gray-400 mr-2 text-xs" />
                Capacidad de Clientes
              </label>
              <ValidatedInput
                type="number"
                name="CapacidadClientes"
                value={formData.CapacidadClientes}
                onChange={handleChange}
                min="1"
                max="20"
                placeholder="1"
                title="Cantidad máxima de clientes simultáneos"
                required
                requiredMessage='Capacidad de clientes es requerida'
              />
              <p className="text-xs text-gray-400 mt-1">Clientes simultáneos</p>
            </div>
          </div>
        </div>
        
        {/* Columna derecha: Ubicación */}
        <div className="bg-gray-800 p-4 rounded-lg border border-gray-600">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <FaMapMarkerAlt className="text-green-400 mr-2" />
            Ubicación
          </h3>
          
          {/* Dirección */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaMapMarkerAlt className="text-gray-400 mr-2 text-xs" />
              Dirección
            </label>
            <AddressAutocomplete
              name="Direccion"
              label=""
              origin="configuracion"
              labelClassName="block text-sm font-medium text-gray-300 mb-1"
              inputClassName="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors pr-10"
              placeholder="Dirección"
              initialValue={formData.Direccion}
              dropdownAbove={false}
              onAddressSelect={(locationData) => {
                setFormData(prev => ({
                  ...prev,
                  Direccion: locationData.address,
                  Ciudad: locationData.city || prev.Ciudad,
                  coordenadas: locationData.coordinates
                }));
              }}
            />
          </div>

          {/* Ciudad */}
          <div className="mb-4">
            <label className="flex items-center text-sm font-medium mb-2">
              <FaMapMarkerAlt className="text-gray-400 mr-2 text-xs" />
              Ciudad
            </label>
            <input
              type="text"
              name="Ciudad"
              value={formData.Ciudad}
              onChange={handleChange}
              className="w-full p-2 rounded-lg bg-gray-700 border border-gray-500 text-white placeholder-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              placeholder="Ciudad"
            />
          </div>

        </div>
      </div>

      {/* Sección de Horarios - Ocupa ambas columnas */}
      <div className="bg-gray-800 p-4 rounded-lg border border-gray-600 mt-6">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <FaClock className="text-purple-400 mr-2" />
          Horarios de Atención
        </h3>
        
        <HorarioSelectorCompact
          name="horarios"
          defaultValue={formData.horarios}
          onChange={handleHorarioChange}
        />
      </div>
      
      {/* Separador visual y botones */}
      <div className="border-t border-gray-600 pt-6 mt-6">
        <div className="flex flex-col md:flex-row gap-3 md:justify-end">
          <Button 
            variant="secondary" 
            type="button" 
            onClick={onCancel}
            className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium order-1"
          >
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            type="submit"
            disabled={loading}
            className="w-full md:w-auto py-2.5 md:px-6 text-sm font-medium flex items-center justify-center gap-2 order-2"
          >
            {!loading && <FaSave />}
            {loading ? 'Guardando...' : (initialData.id ? 'Actualizar' : 'Guardar') + ' Sucursal'}
          </Button>
        </div>
      </div>
      
      {/* Modal de pago para upgrade de plan */}
      <PaymentModal 
        isOpen={showPaymentModal}
        onClose={closePaymentModal}
        targetPlanId={targetPlanId}
      />
    </form>
  );
};

export default SucursalForm;